import React, { useState, useEffect } from 'react';
import { Wand2, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from '../lib/toast';
import { Database } from '../types/supabase';

type Agency = Database['public']['Tables']['agencies']['Row'];

interface ProjectAICreatorProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export default function ProjectAICreator({ onSuccess, onCancel }: ProjectAICreatorProps) {
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [agencies, setAgencies] = useState<Agency[]>([]);

  useEffect(() => {
    fetchAgencies();
  }, []);

  const fetchAgencies = async () => {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setAgencies(data || []);
    } catch (error) {
      console.error('Error fetching agencies:', error);
      toast.error('Failed to load agencies');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setLoading(true);
    try {
      // Split the input by commas
      const parts = prompt.split(',').map(part => part.trim());

      // Extract project information
      const projectName = parts[0] || 'New Project';
      const agencyName = parts[1] || (agencies.length > 0 ? agencies[0].name : '');
      const priority = parts[2]?.toLowerCase() || 'normal';

      // Validate priority
      const validPriorities = ['low', 'normal', 'high', 'urgent'];
      const normalizedPriority = validPriorities.includes(priority.toLowerCase())
        ? priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase()
        : 'Normal';

      // Validate agency exists
      const agencyExists = agencies.some(a => a.name.toLowerCase() === agencyName.toLowerCase());
      if (!agencyExists) {
        throw new Error(`Agency "${agencyName}" not found. Please select an existing agency.`);
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const projectData = {
        name: projectName,
        agency_name: agencyName,
        priority: normalizedPriority,
        user_id: user.id,
      };

      const { error } = await supabase
        .from('projects')
        .insert([projectData]);

      if (error) throw error;

      toast.success('Project created successfully');
      onSuccess();
      setPrompt('');
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
      <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
        Quick Project Creation with AI
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            Describe the project
          </label>
          <textarea
            id="prompt"
            rows={3}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Enter project details separated by commas: Project Name, Agency Name, Priority (Low/Normal/High/Urgent)"
            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
          <p className="mt-2 text-sm text-gray-500">
            Example: Website Redesign, Tech Solutions Inc, High
          </p>
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Available Agencies:</p>
            <p className="text-sm text-gray-500">
              {agencies.map(a => a.name).join(', ')}
            </p>
          </div>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || agencies.length === 0}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin h-5 w-5 mr-2" />
                Creating Project...
              </>
            ) : (
              <>
                <Wand2 className="h-5 w-5 mr-2" /> 
                Create Project
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}